import { defineStore } from 'pinia'
import { fetchMenuData, type MenuData } from '~/queries'
import { useDataCache } from '#nuxt-multi-cache/composables'

type State = {
  main: MenuData | null
  footerPrimary: MenuData | null
  footerSecondary: MenuData | null
  isMobileMenuOpen: boolean
}


function filterTree<T extends { children?: T[]; restrictedCountries: string[] }>(
  items: T[] = [],
  country: string
): T[] {
  return items
    .map((i) => {
      const children = i.children ? filterTree(i.children, country) : undefined;
      return { ...i, children };
    })
    .filter((i) =>
      !(i.restrictedCountries || []).includes(country) || (i.children && i.children.length > 0)
    );
}

export const useMenusStore = defineStore('menus', {
  state: (): State => ({
    main: null,
    footerPrimary: null,
    footerSecondary: null,
    isMobileMenuOpen: false,
  }),
  actions: {
    async fetch(revalidate: boolean = false) {
      if ((this.main && this.footerPrimary && this.footerSecondary) && !revalidate) {
        return {
          main: this.main,
          footerPrimary: this.footerPrimary,
          footerSecondary: this.footerSecondary,
        }
      }

      try {
        const { value, addToCache } = await useDataCache('menu-data')

        let data: any = [];
        if (value && !revalidate) {
          data = value
        } else {
          data = await Promise.all([
            fetchMenuData({ id: 'main-menu' }),
            fetchMenuData({ id: 'footer-primary' }),
            fetchMenuData({ id: 'footer-secondary' }),
          ])
          await addToCache(data)
        }

        this.main = data[0]
        this.footerPrimary = data[1]
        this.footerSecondary = data[2]

        return {
          main: this.main,
          footerPrimary: this.footerPrimary,
          footerSecondary: this.footerSecondary,
        }
      } catch (error) {
        throw createError({
          statusCode: 500,
          statusMessage: 'Error fetching menus data',
        })
      }
    },
    toggleMobileMenu() {
      this.isMobileMenuOpen = !this.isMobileMenuOpen
    },
    closeMobileMenu() {
      this.isMobileMenuOpen = false
    },
  },
  getters: {
    mainMenu(state): MenuData | null {
      const loc = useLocationStore()
      if (!state.main) return null
      return filterTree(state.main, loc.data?.country_code ?? 'N/A')
    },
    footerPrimaryMenu(state): MenuData | null {
      const loc = useLocationStore()
      if (!state.footerPrimary) return null
      return filterTree(state.footerPrimary, loc.data?.country_code ?? 'N/A')
    },
    footerSecondaryMenu(state): MenuData | null {
      const loc = useLocationStore()
      if (!state.footerSecondary) return null
      return filterTree(state.footerSecondary, loc.data?.country_code ?? 'N/A')
    },
  },
})
